import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyCustomValidation,
  getFormValueForField,
  validateFieldWithCustom,
  validateForm,
} from '../utils/validationUtils';

export const useFormValidation = (
  schema,
  formData,
  touched,
  customValidators = {}
) => {
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(true);

  const validateAllFields = useCallback(() => {
    const result = validateForm(schema, formData);
    const mergedErrors = { ...result.errors };
    let mergedValid = result.isValid;

    if (customValidators && typeof customValidators === 'object') {
      Object.keys(customValidators).forEach((name) => {
        const value = getFormValueForField(formData, name);
        const customErr = applyCustomValidation(name, value, customValidators);
        if (customErr) {
          if (!mergedErrors[name]) {
            mergedErrors[name] = customErr;
            mergedValid = false;
          }
        }
      });
    }

    setErrors(mergedErrors);
    setIsValid(mergedValid);
    return { isValid: mergedValid, errors: mergedErrors };
  }, [schema, formData, customValidators]);

  const validateSingleField = useCallback(
    (name, value) => {
      const fieldError = validateFieldWithCustom(
        schema,
        name,
        value,
        customValidators
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));
    },
    [schema, customValidators]
  );

  useEffect(() => {
    Object.keys(touched).forEach((fieldName) => {
      if (touched[fieldName]) {
        const value = getFormValueForField(formData, fieldName);
        validateSingleField(fieldName, value);
      }
    });
  }, [formData, touched, validateSingleField]);

  const memoizedErrors = useMemo(() => errors, [errors]);
  const memoizedIsValid = useMemo(() => isValid, [isValid]);

  return {
    errors: memoizedErrors,
    isValid: memoizedIsValid,
    validateAllFields,
    validateSingleField,
  };
};
